/** @jsxImportSource @emotion/react */
import { Dispatch, SetStateAction, useEffect } from "react";

import { useTranslation } from "react-i18next";

import moment from "moment";

import DownloadActivitesRange from "@/components/Schedules/components/DownloadActivitesRange";
import IncludeDriverDataCheckboxes from "@/components/Schedules/components/IncludeDriverDataCheckboxes";
import { SCHEDULE_PAYLOAD_NAMES } from "@/components/Schedules/types";

import { DATE_FORMATS, VehicleDataTypes } from "../../../../../../../shared";
import DownloadDataTypesCheckboxes from "../../../../../../Schedules/components/DownloadDataTypesCheckboxes";
import { DRIVER_SCOPE, QuickDownloadPayloadDto } from "../types";

interface VehicleQuickDownloadScheduleDialogContentProps {
  quickDownloadPayload: QuickDownloadPayloadDto;
  setInputError: any;
  setQuickDownloadPayload: Dispatch<SetStateAction<QuickDownloadPayloadDto>>;
}

const VehicleQuickDownloadScheduleDialogContent = ({
  quickDownloadPayload,
  setInputError,
  setQuickDownloadPayload
}: VehicleQuickDownloadScheduleDialogContentProps) => {
  const { t } = useTranslation();

  const { fileTypes, activities, driverScope } = quickDownloadPayload;

  const rangeValue =
    activities?.startDate && activities?.endDate
      ? {
          start: new Date(activities.startDate),
          end: new Date(activities.endDate)
        }
      : null;

  const defaultDateValuesWithActivities = {
    startDate: moment().subtract(1, "week").format(DATE_FORMATS.YearMonthDay),
    endDate: moment().format(DATE_FORMATS.YearMonthDay)
  };

  const isActivitiesChecked = fileTypes.includes(VehicleDataTypes.Activities);

  useEffect(() => {
    setQuickDownloadPayload(prevPayload => ({
      ...prevPayload,
      activities: isActivitiesChecked ? defaultDateValuesWithActivities : null
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActivitiesChecked]);

  useEffect(() => {
    setInputError(
      isActivitiesChecked && !rangeValue ? t("Date picker##invalidDate") : ""
    );
  }, [isActivitiesChecked, rangeValue]);

  const handleDataTypes = (value: VehicleDataTypes) => {
    const isAlreadySelected = fileTypes.includes(value)
      ? fileTypes.filter(val => val !== value)
      : [...fileTypes, value];

    setQuickDownloadPayload({
      ...quickDownloadPayload,
      fileTypes: isAlreadySelected
    });
  };

  const handleIncludeDriverData = (
    fieldName: SCHEDULE_PAYLOAD_NAMES,
    value: boolean
  ) => {
    const slot =
      fieldName === SCHEDULE_PAYLOAD_NAMES.Driver1Slot
        ? DRIVER_SCOPE.Slot1
        : DRIVER_SCOPE.Slot2;

    const otherSlot =
      slot === DRIVER_SCOPE.Slot1 ? DRIVER_SCOPE.Slot2 : DRIVER_SCOPE.Slot1;

    let newDriverScope = DRIVER_SCOPE.None;

    if (driverScope === DRIVER_SCOPE.Both) {
      newDriverScope = otherSlot;
    } else if (driverScope === otherSlot && value) {
      newDriverScope = DRIVER_SCOPE.Both;
    } else {
      newDriverScope = value ? slot : DRIVER_SCOPE.None;
    }

    setQuickDownloadPayload(prev => ({
      ...prev,
      driverScope: newDriverScope
    }));
  };

  const handleActivitiesRangeChange = (
    value: { start: Date; end: Date } | null
  ) => {
    const getDate = (dateValue: Date) =>
      moment(dateValue).format(DATE_FORMATS.YearMonthDay);

    setQuickDownloadPayload(previousPayload => ({
      ...previousPayload,
      activities: value
        ? {
            startDate: getDate(value.start),
            endDate: getDate(value.end)
          }
        : null
    }));
  };

  return (
    <>
      <IncludeDriverDataCheckboxes
        driverScope={driverScope}
        handleChange={(fieldName, value) =>
          handleIncludeDriverData(fieldName, value)
        }
      />

      <DownloadDataTypesCheckboxes
        selectedDataTypes={fileTypes}
        handleChange={(_, value) => handleDataTypes(value)}
      />

      {fileTypes.includes(VehicleDataTypes.Activities) && (
        <DownloadActivitesRange
          rangeValue={rangeValue}
          handleActivitiesRangeChange={handleActivitiesRangeChange}
        />
      )}
    </>
  );
};

export default VehicleQuickDownloadScheduleDialogContent;
